import React, {CSSProperties, forwardRef, ReactElement, ReactNode, Ref, useCallback} from 'react';
import {Form as FinalForm, FormRenderProps, useForm as useReactFinalForm} from 'react-final-form';
import {
   FORM_ERROR,
   FormApi,
   FormApiWrapper, FormState,
   Mutator,
   SubmissionErrors,
   ValidationErrors
} from './FinalFormTypes';
import {FormApi as FinalFormApi} from 'final-form';
import {NativeForm} from './NativeForm';
import {FormSpy} from './FormSpy';

/**
 * {@docCategory Form}
 */
export interface FormProps<T = Record<string, any>> {
   /**
    * The underlying form name.  This is used to namespace form field ids for screen readers.
    */
   name: string;

   /**
    * Called when the form data needs to be saved.
    *
    * @param values - The values
    * @param form - The form api
    * @param callback - Optionally used for asynchronous submissions that do not use promises.  Return
    *                   undefined, then call the callback function with any submission errors when the
    *                   submit is complete.
    * @returns For asynchronous submission, a promise that returns SubmissionErrors if any.  Otherwise,
    * for synchronous submission, the SubmissionErrors if any
    */
   onSubmit?: (values: T, form: FormApi<T>, callback?: (errors?: SubmissionErrors) => void) =>
      SubmissionErrors
      | Promise<SubmissionErrors | undefined>
      | undefined
      | void;

   /**
    * The initial values to display in the fields.
    */
   initialValues?: T;

   /**
    * Form level validation function
    * @param values - The values
    */
   validate?: (values: T) => ValidationErrors | Promise<ValidationErrors> | undefined;

   /**
    * Determines if the underlying html form will fill it's parent containers height.
    *
    * @defaultvalue true
    */
   verticalFill?: boolean;

   /**
    * Optional mutators
    */
   mutators?: Record<string, Mutator<T, Partial<T>>>;

   /**
    * Whether or not to disable the mask on save.  Set this to true if some other component is
    * handling the masking.
    *
    * @defaultvalue false
    */
   disableSaveMask?: boolean;

   /**
    * Whether to disable the alert banner. Set to true to hide it.
    * @defaultvalue false
    */
   disableAlertBanner?: boolean;

   /**
    * Whether to disable the beforeUnloadHandler. Set to true to disable it.
    * @defaultvalue false
    */
   disableBeforeUnloadHandler?: boolean;

   /**
    * Sets a className on the panel.
    */
   className?: string;

   /**
    * Sets styles on the panel.
    */
   style?: CSSProperties;

   /**
    * A callback that notifies the listener whether or not the form is currently saving
    * @param saving - true if is saving, false otherwise
    */
   onSavingStateChange?: (saving: boolean) => void;

   /**
    * A callback that notifies the listener whether or not the form has unsaved changes
    * @param event - The dirty state change event object
    */
   onDirtyStateChange?: (event: DirtyStateChangeEvent<T>) => void;

   /**
    * A callback that notifies the listener when the values on the form have changed.
    * @param event - The FormState object
    */
   onValuesChange?: (formState: FormState<T>) => void;

   /**
    * The children of the form
    */
   children?: ReactNode;
}

/**
 * {@docCategory Form}
 */
export interface DirtyStateChangeEvent<T> {

   /**
    * Whether or not the form has unsaved changes
    */
   hasUnsavedChanges: boolean;

   /**
    * A map of the currently dirty form values, which are values that are different than the
    * form's initial values (i.e. they have changed).
    */
   dirtyFormValues: Partial<T>;

   /**
    * The current form values
    */
   formValues: T;

   /**
    * The form api.  Use this to change field values, get information about the state of the form
    * and fields, etc.
    */
   form: FormApi<T>
}

export interface FormContextType {
   name: string;
}

export const FormContext = React.createContext<FormContextType>({
                                                                   name: ""
                                                                });

const subscription = {
   pristine: true,
   submitting: true
};

const onValuesChangeSubscription = {
   values: true
};

export const Form = forwardRef(function Form<T>(props: FormProps<T>, ref: Ref<FormApi<T>>) {
   const {
      name,
      className,
      style,
      disableSaveMask,
      disableAlertBanner,
      disableBeforeUnloadHandler,
      verticalFill,
      onSavingStateChange,
      onDirtyStateChange,
      onValuesChange,
      children,
      onSubmit
   } = props;

   const nativeForm = useCallback((renderProps: FormRenderProps<T>) => {
                                     return (
                                        <NativeForm<T>
                                           {...renderProps}
                                           name={name}
                                           className={className}
                                           style={style}
                                           disableSaveMask={disableSaveMask}
                                           disableAlertBanner={disableAlertBanner}
                                           disableBeforeUnloadHandler={disableBeforeUnloadHandler}
                                           verticalFill={verticalFill}
                                           onSavingStateChange={onSavingStateChange}
                                           onDirtyStateChange={onDirtyStateChange}
                                           ref={ref}
                                        >
                                           {children}
                                           {onValuesChange &&
                                            <FormSpy
                                               subscription={onValuesChangeSubscription}
                                               onChange={onValuesChange as any}
                                            />
                                           }
                                        </NativeForm>
                                     );
                                  },
                                  [name,
                                   className,
                                   style,
                                   disableSaveMask,
                                   disableAlertBanner,
                                   disableBeforeUnloadHandler,
                                   verticalFill,
                                   onSavingStateChange,
                                   onDirtyStateChange,
                                   children,
                                   ref]);

   const onSubmitHandler = useCallback((values: T,
                                        form: FinalFormApi<T>,
                                        callback?: (errors?: SubmissionErrors) => void) => {
      try {
         const result = onSubmit ? onSubmit(values, new FormApiWrapper(form), callback) : undefined;
         if (result !== undefined && (result as any).then) {
            return (result as any).then((success: any) => {
               return success;
            }, (error: any) => {
               return Promise.resolve({
                                         [FORM_ERROR]: error
                                      });
            });
         }

         return Promise.resolve(result);
      }
      catch (error) {
         return {
            [FORM_ERROR]: error
         };
      }
   }, [onSubmit]);

   return (
      <>
         <FinalForm<T, Partial<T>>
            onSubmit={onSubmitHandler}
            initialValues={props.initialValues}
            validate={props.validate}
            validateOnBlur={false} // we handle this at the field level
            mutators={props.mutators}
            subscription={subscription}
            render={nativeForm}
         />
      </>
   );
}) as <T>(props: FormProps<T> & React.RefAttributes<FormApi<T>>) => ReactElement;

export const generateFormFieldId = (formName: string, fieldName?: string) => {
   if (fieldName === undefined) {
      return undefined;
   }

   if (formName === '') {
      // No form, basically
      return fieldName;
   }

   return formName + '--' + fieldName;
};

export function useForm<FormValues = Record<string, any>, InitialFormValues = Partial<FormValues>>(componentName?: string): FormApi<FormValues, InitialFormValues> {
   return new FormApiWrapper(useReactFinalForm(componentName));
}
